/* eslint-disable react/no-unescaped-entities */
import { useState, useRef, useMemo } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { fmtIST } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { UploadSimple, CheckCircle, Warning, ArrowClockwise, Users, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EmployeePicker, QuickAddEmployeeDialog, MarkLeftDialog, UnmatchedActions } from "../components/BiometricUnmatched";
import { fmtDateIN } from "../lib/utils";

/**
 * Biometric Attendance Import (eSSL Four-Punch .xls + Raw Punch CSV)
 *
 *  Step 1 — Upload file → /api/attendance/biometric/dry-run
 *  Step 2 — Preview: matched employees + unmatched rows (map manually)
 *  Step 3 — Apply → /api/attendance/biometric/apply (writes attendance rows +
 *           pending OT rows for HR approval)
 */
function recentMonths() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 13; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ v: `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`, l: x.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
}

export default function BiometricImport() {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [mappings, setMappings] = useState({});                 // { biometric_id: employee_id }
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recomputeHalfDay, setRecomputeHalfDay] = useState(true);
  const [generateOt, setGenerateOt] = useState(true);
  const [otRate, setOtRate] = useState(1.5);
  const [result, setResult] = useState(null);
  const [duplicateBlock, setDuplicateBlock] = useState(null); // { previous_import } from 409
  const [addRow, setAddRow] = useState(null);
  const [leftRow, setLeftRow] = useState(null);
  const [workbook, setWorkbook] = useState(null); // salary workbook dropped here: {file, month, rows}
  const inputRef = useRef(null);

  const importWorkbook = async () => {
    if (!/^\d{4}-\d{2}$/.test(workbook.month || "")) return toast.error("Pick the month (YYYY-MM)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", workbook.file);
      const { data } = await api.post(`/payroll/attendance-summary/${workbook.month}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`HR corrections saved for ${data.rows} employees — payroll for ${workbook.month} will use them`);
      setWorkbook(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Fetch tenant employees once (used for the "Map to employee" dropdown)
  const loadEmployees = async () => {
    try {
      const { data } = await api.get("/employees", { params: { limit: 5000 } });
      setEmployees(Array.isArray(data) ? data : (data.items || data.employees || []));
    } catch (e) { /* silent */ }
  };

  const onFile = async (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!(name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".csv"))) {
      return toast.error("Please upload a .xls, .xlsx, or .csv file");
    }
    // Guard against Cloudflare/browser caching serving a stale bundle.
    // If the toast still fires an error, first click the "Refresh" browser link.
    setBusy(true);
    setFile(f);
    setMappings({});
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("mappings", "{}");
      const { data } = await api.post("/attendance/biometric/dry-run", fd, {
        headers: { "Content-Type": "multipart/form-data" }, silent: true,
      });
      setReport(data);
      setStep("preview");
      loadEmployees();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 422 && det?.error === "salary_workbook") {
        setWorkbook({ file: f, month: det.month || "", rows: det.rows });
        return;
      }
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const rerunDryRun = async (maps = mappings) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mappings", JSON.stringify(maps));
      const { data } = await api.post("/attendance/biometric/dry-run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const applyImport = async (force = false) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mappings", JSON.stringify(mappings));
      fd.append("recompute_half_day", recomputeHalfDay ? "true" : "false");
      fd.append("generate_ot", generateOt ? "true" : "false");
      fd.append("ot_rate_multiplier", String(otRate));
      if (force) fd.append("force", "true");
      const { data } = await api.post("/attendance/biometric/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setDuplicateBlock(null);
      setStep("done");
      toast.success(`Imported ${data.attendance_upserted} attendance rows`);
    } catch (e) {
      // 409 = duplicate file — surface a modal and let admin choose to force
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "duplicate_file") {
        setDuplicateBlock(detail);
        return;
      }
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const restart = () => {
    setStep("upload"); setFile(null); setReport(null);
    setMappings({}); setResult(null);
  };

  const empOptions = useMemo(() => {
    return employees.map((e) => ({
      id: e.id, code: e.employee_code, label: `${e.name} · ${e.employee_code || "no code"}${e.status === "terminated" ? " · left" : ""}`,
    }));
  }, [employees]);
  const matchedIds = useMemo(() => new Set((report?.preview || []).map(p => p.employee_id)), [report]);


  return (
    <AppShell title="Biometric Attendance Import" moduleKey="people">

      {step === "upload" && (
        <>
          <SectionCard
            title="Step 1 · Upload biometric export"
            subtitle="eSSL 'Monthly Status Report — Four Punch' (.xls) or raw punch CSV">
            <div
              className="border-2 border-dashed border-zinc-300 rounded-lg p-12 text-center hover:border-zinc-500 transition cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              data-testid="bio-dropzone"
            >
              <UploadSimple size={36} className="mx-auto text-zinc-400 mb-2"/>
              <div className="text-sm font-medium text-zinc-700">Drop the file here</div>
              <div className="text-xs text-zinc-500 mt-1">.xls (four-punch monthly report) or .csv (raw punches)</div>
              <input ref={inputRef} type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={e => onFile(e.target.files?.[0])}/>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-3 text-xs">
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3">
                <div className="font-semibold text-emerald-800 mb-1">eSSL Four-Punch (.xls)</div>
                <div className="text-emerald-900/80">Monthly Status Report → Detailed → Four-Punch. Employees are matched by fuzzy name; unknown biometric IDs get a manual mapping row.</div>
              </div>
              <div className="border border-zinc-200 bg-zinc-50 rounded-md p-3">
                <div className="font-semibold text-zinc-800 mb-1">Raw punch CSV</div>
                <div className="text-zinc-600">Columns: <code>employee_code, employee_name, timestamp</code> — one row per punch. In/out grouped automatically per day.</div>
              </div>
            </div>
            {busy && <div className="mt-4 text-sm text-zinc-500 flex items-center gap-2"><ArrowClockwise size={14} className="animate-spin"/> Parsing…</div>}
            {workbook && (
              <div className="mt-4 border border-blue-200 bg-blue-50/60 rounded-md p-4" data-testid="bio-workbook-card">
                <div className="font-semibold text-sm text-blue-900">This is a monthly salary / attendance workbook ({workbook.rows} employees)</div>
                <div className="text-xs text-blue-900/80 mt-1">It holds HR's corrections (Total Payable Days, Night Present, OT), not device punches. Save it as HR corrections for the month — payroll uses them on top of the eSSL punches already imported.</div>
                <div className="flex items-end gap-2 mt-3">
                  <div><div className="text-[11px] text-zinc-600 mb-1">Month</div>
                    <select value={workbook.month} onChange={e => setWorkbook({ ...workbook, month: e.target.value })}
                      className="h-9 w-44 border border-zinc-300 rounded px-2 text-sm bg-white" data-testid="bio-workbook-month">
                      <option value="">Pick month</option>
                      {recentMonths().map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select></div>
                  <Button size="sm" className="h-9" onClick={importWorkbook} disabled={busy} data-testid="bio-workbook-import">Save as HR corrections</Button>
                  <Button size="sm" variant="ghost" className="h-9" onClick={() => setWorkbook(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </SectionCard>
        </>
      )}

      {step === "preview" && report && (
        <>
          {/* Duplicate-file / overlapping-period warning */}
          {report.duplicate_warning?.exact_match && (
            <div
              className="mb-3 border border-rose-300 bg-rose-50 rounded-lg p-4 flex items-start gap-3"
              data-testid="bio-dup-exact-banner"
            >
              <Warning size={20} className="text-rose-600 shrink-0 mt-0.5"/>
              <div className="text-sm">
                <div className="font-semibold text-rose-900">
                  You already imported this exact file
                </div>
                <div className="text-rose-800/90 mt-0.5">
                  Uploaded on <b>{fmtIST(report.duplicate_warning.exact_match.created_at)} IST</b>
                  {" "}by <b>{report.duplicate_warning.exact_match.actor_name || "someone"}</b> ·
                  {" "}{report.duplicate_warning.exact_match.attendance_upserted} attendance rows,
                  {" "}{report.duplicate_warning.exact_match.ot_pending_created} OT rows.
                  Applying again will overwrite attendance and refresh (not duplicate) pending OT.
                </div>
              </div>
            </div>
          )}
          {report.duplicate_warning?.overlap_match && !report.duplicate_warning?.exact_match && (
            <div
              className="mb-3 border border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
              data-testid="bio-dup-overlap-banner"
            >
              <Warning size={20} className="text-amber-600 shrink-0 mt-0.5"/>
              <div className="text-sm">
                <div className="font-semibold text-amber-900">
                  This period was already imported from a different file
                </div>
                <div className="text-amber-800/90 mt-0.5">
                  On <b>{fmtIST(report.duplicate_warning.overlap_match.created_at)} IST</b>
                  {" "}({report.duplicate_warning.overlap_match.period_start} → {report.duplicate_warning.overlap_match.period_end},
                  {" "}{report.duplicate_warning.overlap_match.filename}).
                  Applying will overwrite attendance for overlapping days and replace any pending OT.
                </div>
              </div>
            </div>
          )}

          {/* Sticky top bar — visible even when SectionCard header wraps */}
          <div className="sticky top-0 z-20 bg-white border border-zinc-200 rounded-lg p-3 mb-3 flex items-center justify-between gap-2 shadow-sm">
            <div className="text-sm">
              <span className="font-medium">Ready to apply</span>
              <span className="text-zinc-500"> · {report.matched_count} matched · {report.unmatched_count} unmatched</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => rerunDryRun()} disabled={busy} data-testid="bio-refresh-top">
                {busy ? <ArrowClockwise size={14} className="animate-spin"/> : "Refresh"}
              </Button>
              <Button size="sm" variant="outline" onClick={restart}>Cancel</Button>
              <Button size="sm" onClick={() => applyImport()} disabled={busy || report.matched_count === 0} data-testid="bio-apply-top">
                {busy ? "Applying…" : `Apply (${report.matched_count} employees)`}
              </Button>
            </div>
          </div>

          <SectionCard
            title={`Step 2 · Preview · ${report.period_start} → ${report.period_end}`}
            subtitle={`${report.matched_count} employees matched · ${report.unmatched_count} unmatched · format: ${report.format}`}
            action={
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => rerunDryRun()} disabled={busy} data-testid="bio-refresh">
                  {busy ? <ArrowClockwise size={14} className="animate-spin"/> : "Refresh preview"}
                </Button>
                <Button size="sm" variant="outline" onClick={restart}>Cancel</Button>
                <Button size="sm" onClick={() => applyImport()} disabled={busy || report.matched_count === 0} data-testid="bio-apply">
                  Apply ({report.matched_count} employees)
                </Button>
              </div>
            }
          >
            {/* Import options */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="border border-zinc-200 rounded-md p-3 flex items-center justify-between">
                <div className="text-sm">
                  <div className="font-medium">Recompute half-day/late from Duration</div>
                  <div className="text-xs text-zinc-500">Uses SaarOne shift grace + min-hours rules</div>
                </div>
                <Switch checked={recomputeHalfDay} onCheckedChange={setRecomputeHalfDay} data-testid="bio-recompute"/>
              </div>
              <div className="border border-zinc-200 rounded-md p-3 flex items-center justify-between">
                <div className="text-sm">
                  <div className="font-medium">Generate overtime (pending HR approval)</div>
                  <div className="text-xs text-zinc-500">Extracted from OT column + hours beyond full-day threshold</div>
                </div>
                <Switch checked={generateOt} onCheckedChange={setGenerateOt} data-testid="bio-ot"/>
              </div>
              <div className="border border-zinc-200 rounded-md p-3 flex items-center gap-3">
                <div className="text-sm flex-1">
                  <div className="font-medium">OT rate multiplier</div>
                  <div className="text-xs text-zinc-500">India factory norm: 1.5× or 2×</div>
                </div>
                <Input type="number" step="0.1" min="1" max="3" value={otRate}
                  onChange={e => setOtRate(parseFloat(e.target.value) || 1.5)}
                  className="w-20 h-9" data-testid="bio-ot-rate"/>
              </div>
            </div>

            {/* Unmatched employees — map manually */}
            {report.unmatched?.length > 0 && (
              <div className="mb-4 border border-amber-200 bg-amber-50/50 rounded-md p-3">
                <div className="flex items-center gap-2 text-amber-900 mb-2">
                  <Warning size={16}/>
                  <span className="font-semibold text-sm">{report.unmatched.length} unmatched biometric row(s) — map to an employee to include them</span>
                </div>
                <div className="grid gap-2">
                  {report.unmatched.map((u) => (
                    <div key={u.biometric_id} className="flex gap-3 items-center text-sm" data-testid={`bio-unmapped-${u.biometric_id}`}>
                      <div className="w-8 font-mono text-xs text-zinc-600">#{u.biometric_id}</div>
                      <div className="w-56">
                        <div className="truncate font-medium">{u.biometric_name}</div>
                        <div className="text-[11px] text-zinc-500">{u.days_punched ? `${u.days_punched} days punched · ${fmtDateIN(u.first_punch)} – ${fmtDateIN(u.last_punch)}` : "No punches this month"}</div>
                      </div>
                      <div className="flex-1">
                        <EmployeePicker options={empOptions.filter(o => !matchedIds.has(o.id) || o.id === mappings[u.biometric_id])} value={mappings[u.biometric_id] || ""}
                          onChange={(v) => setMappings(m => ({ ...m, [u.biometric_id]: v }))} testid={`bio-map-${u.biometric_id}`}/>
                      </div>
                      <UnmatchedActions id={u.biometric_id} onAdd={() => setAddRow(u)} onLeft={() => setLeftRow(u)}/>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => rerunDryRun()} disabled={busy} data-testid="bio-apply-mappings">Apply mappings & refresh</Button>
                </div>
              </div>
            )}
            {report.ignored_count > 0 && (
              <div className="mb-4 text-xs text-zinc-500" data-testid="bio-ignored-count">{report.ignored_count} device user(s) marked as left are hidden from this list.</div>
            )}
            <QuickAddEmployeeDialog row={addRow} onClose={() => setAddRow(null)} onAdded={async (bid, emp) => {
              setAddRow(null); await loadEmployees();
              const maps = { ...mappings, [bid]: emp.employee_id }; setMappings(maps); rerunDryRun(maps);
            }}/>
            <MarkLeftDialog row={leftRow} employees={empOptions} onClose={() => setLeftRow(null)} onDone={() => { setLeftRow(null); rerunDryRun(); }}/>

            {/* Matched preview table */}
            <div className="overflow-x-auto border border-zinc-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium">Employee</th>
                    <th className="text-left py-2 px-3 font-medium">Biometric #</th>
                    <th className="text-left py-2 px-3 font-medium">Match</th>
                    <th className="text-right py-2 px-3 font-medium">Present</th>
                    <th className="text-right py-2 px-3 font-medium">Absent</th>
                    <th className="text-right py-2 px-3 font-medium">WO</th>
                    <th className="text-right py-2 px-3 font-medium">Total hours</th>
                    <th className="text-right py-2 px-3 font-medium">OT (h)</th>
                  </tr>
                </thead>
                <tbody>
                  {report.preview.map((r, i) => (
                    <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2 px-3">
                        <div className="font-medium">{r.employee_name}{r.left_on && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" data-testid={`bio-left-badge-${r.biometric_id}`}>Left {fmtDateIN(r.left_on)}</span>}</div>
                        <div className="text-xs text-zinc-500">{r.employee_code}</div>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs">#{r.biometric_id} · {r.biometric_name}</td>
                      <td className="py-2 px-3 text-xs">
                        <span className={`inline-block px-1.5 py-0.5 rounded ${
                          r.match_method === "biometric_id" ? "bg-emerald-100 text-emerald-800" :
                          r.match_method === "override" ? "bg-blue-100 text-blue-800" :
                          "bg-amber-100 text-amber-800"
                        }`}>
                          {r.match_method}{r.match_score ? ` ${r.match_score}%` : ""}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{r.days_present}</td>
                      <td className="py-2 px-3 text-right font-mono text-rose-700">{r.days_absent}</td>
                      <td className="py-2 px-3 text-right font-mono text-zinc-400">{r.days_week_off}</td>
                      <td className="py-2 px-3 text-right font-mono">{r.monthly_total_hours}</td>
                      <td className="py-2 px-3 text-right font-mono text-orange-700">{r.ot_hours_reported || 0}</td>
                    </tr>
                  ))}
                  {report.preview.length === 0 && (
                    <tr><td colSpan="8" className="text-center py-8 text-zinc-500">No matched employees. Map at least one row above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}

      {step === "done" && result && (
        <SectionCard title="Import complete" subtitle={`${result.period_start} → ${result.period_end}`}>
          <div className="grid md:grid-cols-4 gap-3 text-sm">
            <Stat label="Attendance rows written" value={result.attendance_upserted} icon={CheckCircle} tone="emerald"/>
            <Stat label="Employees matched" value={result.matched} icon={Users}/>
            <Stat label="Days marked absent" value={result.days_absent_marked} tone="rose"/>
            <Stat label="Overtime pending approval" value={result.overtime_pending} icon={Clock} tone="orange"/>
          </div>
          <div className="mt-4 text-sm text-zinc-600">
            Attendance is now live. When you run payroll for this month, LOP will be computed automatically
            for employees with a biometric ID (missing punches → unpaid days).
            OT rows need to be approved from <a className="underline" href="/app/overtime-approvals">Overtime Approvals</a> before they flow to payroll.
          </div>
          <div className="mt-4">
            <Button onClick={restart} variant="outline" size="sm">Import another file</Button>
          </div>
        </SectionCard>
      )}

      {/* Duplicate-file confirmation modal (raised when apply returns 409) */}
      {duplicateBlock && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="bio-dup-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} className="text-rose-600"/>
              <h3 className="text-lg font-semibold">Duplicate file detected</h3>
            </div>
            <p className="text-sm text-zinc-700 mb-4">
              {duplicateBlock.message}
            </p>
            {duplicateBlock.previous_import && (
              <div className="border border-zinc-200 rounded p-3 mb-4 text-xs bg-zinc-50">
                <div><b>File:</b> {duplicateBlock.previous_import.filename}</div>
                <div><b>Period:</b> {duplicateBlock.previous_import.period_start} → {duplicateBlock.previous_import.period_end}</div>
                <div><b>By:</b> {duplicateBlock.previous_import.actor_name || "—"}</div>
                <div><b>Attendance rows:</b> {duplicateBlock.previous_import.attendance_upserted} · <b>OT rows:</b> {duplicateBlock.previous_import.ot_pending_created}</div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setDuplicateBlock(null)} data-testid="bio-dup-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                onClick={() => { setDuplicateBlock(null); applyImport(true); }}
                data-testid="bio-dup-force"
              >
                Force re-import
              </Button>
            </div>
            <div className="mt-3 text-[11px] text-zinc-500">
              Re-importing is safe: attendance rows upsert per employee-day, and pending overtime is
              refreshed (not duplicated). Approved/rejected OT is untouched.
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}


function Stat({ label, value, icon: Icon, tone = "zinc" }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    orange: "border-orange-200 bg-orange-50 text-orange-900",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-800",
  };
  return (
    <div className={`border rounded-md p-3 ${tones[tone] || tones.zinc}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
        {Icon && <Icon size={14}/>} {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
